import { useState, useEffect } from 'react';

interface SiteImage {
  id: string;
  section: string;
  image_key: string;
  image_url: string;
  alt_text: string;
}

export default function NosotrosPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [images, setImages] = useState<{ [key: string]: string }>();

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      const response = await fetch('https://qdnmvfldkrvckefmtxhj.supabase.co/functions/v1/get-site-images');
      const result = await response.json();
      if (result.success) {
        const imageMap: { [key: string]: string } = {};
        result.data.forEach((img: SiteImage) => {
          imageMap[`${img.section}-${img.image_key}`] = img.image_url;
        });
        setImages(imageMap);
      }
    } catch (error) {
      console.error('Error al cargar imágenes:', error);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Encabezado Superior */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center text-xs sm:text-sm gap-2">
          <div className="flex items-center gap-2">
            <i className="ri-map-pin-line"></i>
            <span className="text-center sm:text-left">Servicio de entrega de agua purificada a domicilio</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform cursor-pointer">
              <i className="ri-facebook-fill text-lg"></i>
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform cursor-pointer">
              <i className="ri-instagram-line text-lg"></i>
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform cursor-pointer">
              <i className="ri-twitter-x-line text-lg"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Navegación */}
      <nav className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white shadow-md'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <a className="flex items-center gap-3 cursor-pointer" href="/">
              <img
                src={images?.['logo-main'] || 'https://static.readdy.ai/image/61b09db2ffc810402633d52f1dcabae9/9ec598441a0c5e4bb295dc2ea34728b5.jpeg'}
                alt="PUREZA Logo"
                className="h-12 sm:h-16 w-auto object-contain"
              />
            </a>

            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <a href="/" className="text-gray-700 hover:text-blue-900 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm xl:text-base">Inicio</a>
              <a href="#historia" className="text-gray-700 hover:text-blue-900 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm xl:text-base">Nuestra Historia</a>
              <a href="#certificacion" className="text-gray-700 hover:text-blue-900 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm xl:text-base">Certificación INVIMA</a>
              <a href="/" className="px-4 xl:px-6 py-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-sm xl:text-base">Volver al Inicio</a>
            </div>

            <button className="lg:hidden text-gray-700 text-2xl cursor-pointer" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t">
              <div className="flex flex-col gap-4">
                <a href="/" className="text-gray-700 hover:text-blue-900 transition-colors font-medium cursor-pointer">Inicio</a>
                <a href="#historia" className="text-gray-700 hover:text-blue-900 transition-colors font-medium cursor-pointer">Nuestra Historia</a>
                <a href="#certificacion" className="text-gray-700 hover:text-blue-900 transition-colors font-medium cursor-pointer">Certificación INVIMA</a>
                <a href="/" className="px-6 py-2 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-center">Volver al Inicio</a>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-[300px] sm:h-[400px] lg:h-[450px] overflow-hidden">
        <img
          alt="Agua PUREZA certificada INVIMA"
          className="w-full h-full object-cover object-center"
          src="https://static.readdy.ai/image/61b09db2ffc810402633d52f1dcabae9/6660ca62fcaa507a25d8d8a978d7da02.png"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-4 w-full">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 drop-shadow-lg">
              Sobre Nosotros
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl font-light drop-shadow-lg max-w-2xl mx-auto">
              Más de 5 años brindando agua de la más alta calidad, certificada y segura para tu familia
            </p>
          </div>
        </div>
      </section>

      {/* Historia y Misión */}
      <section id="historia" className="py-12 sm:py-16 lg:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div>
              <img
                alt="Instalaciones PUREZA"
                className="w-full h-auto rounded-lg shadow-xl"
                src="https://static.readdy.ai/image/61b09db2ffc810402633d52f1dcabae9/a5299d15a16f93cd2091ce7a8a27c576.png"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent">
                Nuestra Historia
              </h2>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4">
                PUREZA nació con la misión de llevar agua purificada de la más alta calidad a cada hogar de López de Micay y sus alrededores. Somos una empresa comprometida con la salud y el bienestar de nuestra comunidad.
              </p>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-4">
                A través de los años, hemos perfeccionado nuestros procesos de purificación para garantizar que cada gota de agua que llega a tu mesa cumpla con los estándares más exigentes de calidad e inocuidad.
              </p>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6">
                Nuestro compromiso va más allá de vender agua: queremos ser tu aliado en la salud de tu familia, ofreciendo un producto seguro, confiable y siempre fresco.
              </p>
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-blue-900">100%</div>
                  <div className="text-sm text-gray-600">Agua certificada</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-blue-900">100%</div>
                  <div className="text-sm text-gray-600">Clientes satisfechos</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sección Certificación INVIMA */}
      <section id="certificacion" className="py-12 sm:py-16 lg:py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 bg-gradient-to-br from-green-600 to-emerald-500 rounded-full flex items-center justify-center">
              <i className="ri-shield-check-line text-white text-4xl sm:text-5xl"></i>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent">
              Certificación INVIMA
            </h2>
            <p className="text-lg sm:text-xl text-gray-700 max-w-3xl mx-auto">
              Nuestro agua purificada cuenta con la autorización oficial del Instituto Nacional de Vigilancia de Medicamentos y Alimentos de Colombia
            </p>
          </div>

          {/* Tarjeta de información del registro */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-8 sm:mb-10">
            <div className="bg-gradient-to-r from-green-700 to-emerald-600 px-6 sm:px-8 py-4 sm:py-5">
              <h3 className="text-white text-lg sm:text-xl font-bold flex items-center gap-2">
                <i className="ri-file-shield-line"></i>
                Resolución No. 2025068892
              </h3>
              <p className="text-green-100 text-sm mt-1">Fecha: 29 de diciembre de 2025</p>
            </div>
            <div className="p-6 sm:p-8">
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Número de Autorización</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">RSA-4313-2025</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Nombre del Producto</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">AGUA POTABLE TRATADA PUREZA</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Titular del Registro</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">URCA ALL SERVICES</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Marca Registrada</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">PUREZA</p>
                </div>
              </div>
              <div className="border-t pt-5">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Dirección del Titular</p>
                <p className="text-base sm:text-lg font-semibold text-gray-900 mb-1">Carrera 1 # 31-5</p>
                <p className="text-gray-600">López de Micay, Cauca, Colombia</p>
              </div>
            </div>
          </div>

          {/* PDF Documento */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 sm:px-8 py-5 sm:py-6 border-b border-gray-100">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Documento INVIMA Oficial</h3>
              <p className="text-sm text-gray-600">Resolución de modificación de autorización de comercialización</p>
            </div>
            <div className="p-6 sm:p-8 flex flex-col items-center text-center gap-5">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
                <i className="ri-file-pdf-2-line text-red-600 text-3xl"></i>
              </div>
              <div>
                <p className="text-gray-700 text-base sm:text-lg font-medium">
                  Resolución INVIMA No. 2025068892
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Haz clic para abrir el documento oficial en una nueva pestaña
                </p>
              </div>
              <button
                onClick={() => window.open('https://storage.readdy-site.link/project_files/1d30528c-60d8-474b-a7b5-036af303bc9e/c6a6d710-b976-4392-b20b-1f973aa5d9d5_invima.pdf?v=8e7ac9b8d62d0932ce70e210d790557d', '_blank', 'noopener,noreferrer')}
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-full hover:shadow-xl transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base font-medium"
              >
                <i className="ri-external-link-line"></i>
                Ver documento INVIMA
              </button>
            </div>
          </div>

          {/* Información adicional sobre INVIMA */}
          <div className="mt-10 sm:mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-blue-900 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">¿Qué es INVIMA?</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                El Instituto Nacional de Vigilancia de Medicamentos y Alimentos es la autoridad sanitaria de Colombia encargada de regular y controlar la calidad de alimentos y bebidas.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-blue-900 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">¿Qué significa el registro?</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                La Autorización de Comercialización (RSA) certifica que nuestro agua cumple con todos los requisitos de calidad e inocuidad establecidos por la normatividad sanitaria colombiana.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <i className="ri-water-flash-line text-blue-900 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Modalidad Autorizada</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                Estamos autorizados para fabricar, envasar y vender agua potable tratada bajo la marca PUREZA, garantizando calidad en todo el proceso.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative">
        <div className="w-full">
          <svg className="w-full h-auto" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z" fill="#1e3a8a" />
          </svg>
        </div>
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-12 sm:py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12">
              <div>
                <div className="flex items-center gap-3 mb-6 sm:mb-8">
                  <img
                    src={images?.['logo-main'] || 'https://static.readdy.ai/image/61b09db2ffc810402633d52f1dcabae9/9ec598441a0c5e4bb295dc2ea34728b5.jpeg'}
                    alt="PUREZA Logo"
                    className="h-12 sm:h-16 w-auto object-contain"
                  />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Contáctanos</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-phone-line text-xl sm:text-2xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-sm sm:text-base">¡Listos siempre para atenderte!</p>
                      <p className="text-base sm:text-lg">+57&nbsp;318 064 8369</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-mail-line text-xl sm:text-2xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-sm sm:text-base">¡Estamos interesados en tus pedidos!</p>
                      <p className="text-base sm:text-lg break-all">charubano@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-map-pin-line text-xl sm:text-2xl mt-1"></i>
                    <div>
                      <p className="font-semibold text-sm sm:text-base">Dirección principal</p>
                      <p className="text-base sm:text-lg">Calle San Francisco - Malecón</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
                  ¿Quieres recibir más<br />información de <strong>PUREZA?</strong>
                </h3>
                <form className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-8">
                  <input
                    placeholder="Email"
                    required
                    className="flex-1 px-4 py-3 rounded-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-white text-sm"
                    type="email"
                  />
                  <button type="submit" className="px-6 sm:px-8 py-3 bg-white text-blue-900 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer text-sm sm:text-base">
                    Enviar
                  </button>
                </form>
                <h3 className="text-xl sm:text-2xl font-bold mb-4">Síguenos en redes</h3>
                <div className="flex gap-4">
                  <a href="https://www.facebook.com/profile.php?id=61585733834974" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer">
                    <i className="ri-facebook-fill text-blue-900 text-xl"></i>
                  </a>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer">
                    <i className="ri-instagram-line text-blue-900 text-xl"></i>
                  </a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white rounded-full flex items-center justify-center hover:scale-110 transition-transform cursor-pointer">
                    <i className="ri-twitter-x-line text-blue-900 text-xl"></i>
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/20 text-center">
              <p className="text-xs sm:text-sm">
                © 2025 PUREZA. Todos los derechos reservados. |{' '}
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:underline cursor-pointer">Charles Urbano</a>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}